import { useState, useEffect } from "react";
import { Calendar } from "@/components/Calendar";
import { MetricsCard } from "@/components/MetricsCard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { type Meeting, type Client } from "@/data/mockData";
import { format, parseISO, addHours } from "date-fns";
import { Users, TrendingUp, Calendar as CalendarIcon, Plus, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { clientApi, meetingApi, metricsApi } from "@/services/api";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [newMeetingDialog, setNewMeetingDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState("");
  const [meetingType, setMeetingType] = useState("");
  const [meetingDate, setMeetingDate] = useState("");
  const [meetingTime, setMeetingTime] = useState("");
  const [notes, setNotes] = useState("");

  const [clients, setClients] = useState<Client[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [metrics, setMetrics] = useState({
    clientGrowth: [],
    revenue: [],
    clientRetention: 0,
    newClientsThisMonth: 0,
    upcomingMeetings: 0,
  });

  const [loading, setLoading] = useState({
    clients: true,
    meetings: true,
    metrics: true
  });

  const navigate = useNavigate();

  // Fetch clients from API
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await clientApi.getAll();
        setClients(response);
      } catch (error) {
        console.error("Error fetching clients:", error);
        toast({
          title: "Error",
          description: "Failed to load clients. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(prev => ({ ...prev, clients: false }));
      }
    };

    fetchClients();
  }, []);

  // Fetch meetings from API
  useEffect(() => {
    const fetchMeetings = async () => {
      try {
        const response = await meetingApi.getAll();

        // The API populates `client` as an object ({ _id, name, ... }), but it
        // may also come back as a plain ID string. Handle both, derive the
        // client name, and normalize `client` to the ID string for downstream
        // components (e.g. Calendar) that expect an ID.
        const meetingsWithClientNames = response.map(meeting => {
          const populatedClient =
            meeting.client && typeof meeting.client === 'object'
              ? (meeting.client as any)
              : null;

          const clientId = populatedClient ? populatedClient._id : meeting.client;

          const matchedClient = clients.find(c =>
            (c._id || c.id) === clientId
          );

          return {
            ...meeting,
            client: clientId,
            clientName: populatedClient?.name || matchedClient?.name || 'Unknown Client'
          };
        });

        setMeetings(meetingsWithClientNames);
      } catch (error) {
        console.error("Error fetching meetings:", error);
        toast({
          title: "Error",
          description: "Failed to load meetings. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(prev => ({ ...prev, meetings: false }));
      }
    };

    if (clients.length > 0) {
      fetchMeetings();
    } else {
      // No clients: ensure meetings loading state completes and UI stays functional
      setMeetings([]);
      setLoading(prev => ({ ...prev, meetings: false }));
    }
  }, [clients]);

  // Fetch dashboard metrics from API
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        // Try to get business metrics first
        const businessMetrics = await metricsApi.getBusinessMetrics();

        // If that fails or doesn't have all data, get dashboard stats
        const dashboardStats = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/metrics/dashboard`).then(res => res.json());

        setMetrics({
          clientGrowth: businessMetrics?.clientGrowth || [],
          revenue: businessMetrics?.revenue || [],
          clientRetention: businessMetrics?.clientRetention || 0,
          newClientsThisMonth: dashboardStats?.newClientsThisMonth || 0,
          upcomingMeetings: dashboardStats?.upcomingMeetings || 0
        });
      } catch (error) {
        console.error("Error fetching metrics:", error);
        // Set fallback metrics
        setMetrics({
          clientGrowth: [{ month: "Current", clients: clients.length }],
          revenue: [{ month: "Current", amount: 0 }],
          clientRetention: 0,
          newClientsThisMonth: 0,
          upcomingMeetings: meetings.length
        });
      } finally {
        setLoading(prev => ({ ...prev, metrics: false }));
      }
    };

    if (!loading.clients && !loading.meetings) {
      fetchMetrics();
    }
  }, [loading.clients, loading.meetings, clients.length, meetings.length]);

  const handleNewMeeting = () => {
    setNewMeetingDialog(true);
  };

  const handleCreateMeeting = async () => {
    try {
      // Validate required fields
      if (!selectedClient || !meetingDate || !meetingTime || !meetingType) {
        toast({
          title: "Required Fields",
          description: "Please fill in all required fields",
          variant: "destructive",
        });
        return;
      }

      // Parse date and time
      const [year, month, day] = meetingDate.split("-").map(Number);
      const [hours, minutes] = meetingTime.split(":").map(Number);

      const startTime = new Date(year, month - 1, day, hours, minutes);
      const endTime = addHours(startTime, 1); // Default 1-hour meeting

      // Find client for title and name
      const client = clients.find(c => (c._id || c.id) === selectedClient);
      const title = `${meetingType} - ${client?.name || 'Client'}`;

      // Create meeting via API (do not send clientName)
      const newMeeting = await meetingApi.create({
        client: selectedClient, // For MongoDB
        title,
        startTime,
        endTime,
        type: meetingType as any,
        notes: notes
      });

      // Add the new meeting to the list, include clientName for local display
      setMeetings(prev => [...prev, { ...newMeeting, clientName: client?.name || '' }]);

      // Reset form data
      setSelectedClient("");
      setMeetingDate("");
      setMeetingTime("");
      setMeetingType("");
      setNotes("");

      // Close the dialog
      setNewMeetingDialog(false);

      toast({
        title: "Meeting Created",
        description: "New meeting has been scheduled successfully.",
        variant: "default",
      });
    } catch (error) {
      console.error("Error creating meeting:", error);
      toast({
        title: "Error",
        description: "Failed to create meeting. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Calculate upcoming and total meetings
  const now = new Date();
  const upcomingMeetingsCount = meetings.filter(m => new Date(m.startTime) > now).length;
  const totalMeetingsCount = meetings.length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">
            Monitor your schedule and business metrics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleNewMeeting}>
            <Plus className="mr-2 h-4 w-4" /> New Meeting
          </Button>
        </div>
      </div>

      {loading.metrics ? (
        <div className="flex justify-center items-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <MetricsCard
            title="Total Clients"
            value={clients.length.toString()}
            description="Active clients in your practice"
            data={metrics.clientGrowth}
            dataKey="clients"
            chartColor="#4CAF50"
            // percentageChange={10}
            icon={<Users className="h-4 w-4 text-muted-foreground" />}
            onClick={() => navigate("/LODYClients")}
            className="focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <MetricsCard
            title="Monthly Revenue"
            value={metrics.revenue.length > 0
              ? `$${metrics.revenue[metrics.revenue.length - 1].amount}`
              : "$0"}
            description="Income this month"
            data={metrics.revenue}
            dataKey="amount"
            chartColor="#FFA726"
            // percentageChange={5}
            icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
            onClick={() => navigate("/LODYFinance")}
            className="focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <MetricsCard
            title="Upcoming Appointments"
            value={upcomingMeetingsCount.toString()}
            description="Meetings in the future"
            // percentageChange={3}
            dataKey="sessions"
            icon={<CalendarIcon className="h-4 w-4 text-muted-foreground" />}
            onClick={() => navigate("/admin")}
            className="focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <MetricsCard
            title="Total Meetings"
            value={totalMeetingsCount.toString()}
            description="All scheduled meetings"
            // percentageChange={undefined}
            dataKey="sessions"
            icon={<CalendarIcon className="h-4 w-4 text-muted-foreground" />}
            onClick={() => navigate("/admin")}
            className="focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      )}

      {loading.meetings ? (
        <div className="flex justify-center items-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div>
          <Calendar
            meetings={meetings}
            onSelectMeeting={setSelectedMeeting}
            onMeetingDeleted={(meetingId) => {
              setMeetings(prev => prev.filter(m => m._id !== meetingId));
            }}
          />
        </div>
      )}

      <Dialog open={!!selectedMeeting} onOpenChange={() => setSelectedMeeting(null)}>
        {selectedMeeting && (
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{selectedMeeting.title}</DialogTitle>
            </DialogHeader>
            <div className="mt-4 space-y-3">
              <div className="flex flex-col gap-1">
                <span className="text-sm text-gray-500">Date & Time</span>
                <p>
                  {format(new Date(selectedMeeting.startTime), "MMMM d, yyyy")} at{" "}
                  {format(new Date(selectedMeeting.startTime), "h:mm a")} -{" "}
                  {format(new Date(selectedMeeting.endTime), "h:mm a")}
                </p>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-sm text-gray-500">Meeting Type</span>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full 
                    ${selectedMeeting.type === "initial" ? "bg-nutrition-green-500" :
                      selectedMeeting.type === "follow-up" ? "bg-nutrition-orange-400" : "bg-blue-500"}`}
                  />
                  <p className="capitalize">{selectedMeeting.type}</p>
                </div>
              </div>

              {selectedMeeting.notes && (
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-gray-500">Notes</span>
                  <p>{selectedMeeting.notes}</p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setSelectedMeeting(null)}>Close</Button>
                <Button onClick={() => {
                  setSelectedMeeting(null);
                  toast({
                    title: "Meeting Updated",
                    description: "Meeting has been updated successfully.",
                  });
                }}>Edit Meeting</Button>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>

      <Dialog open={newMeetingDialog} onOpenChange={setNewMeetingDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Schedule New Meeting</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="client" className="text-right">
                Client*
              </Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client._id || client.id} value={client._id || client.id || ""}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="date" className="text-right">
                Date*
              </Label>
              <Input
                id="date"
                type="date"
                className="col-span-3"
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="time" className="text-right">
                Time*
              </Label>
              <Input
                id="time"
                type="time"
                className="col-span-3"
                value={meetingTime}
                onChange={(e) => setMeetingTime(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="type" className="text-right">
                Meeting Type*
              </Label>
              <Select value={meetingType} onValueChange={setMeetingType}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="initial">Initial Consultation</SelectItem>
                  <SelectItem value="follow-up">Follow-up</SelectItem>
                  <SelectItem value="assessment">Assessment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="notes" className="text-right">
                Notes
              </Label>
              <Input
                id="notes"
                className="col-span-3"
                placeholder="Add meeting notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewMeetingDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateMeeting}>
              Schedule Meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
